import { useEffect, useRef, useState } from "react";
import { usageToDirectionIcon } from "../../../utils/bitmaskToIcon";
import { UsageMap } from "../../../types/monitoringTypes";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { debounceKey, DEVICE_ELEVATOR, DEVICE_ESCALATOR } from "../../../utils/debounceLookup";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { tracePointEmit } from "../../../integrationTrace";
import { TracePointId } from "../../../flatbuffers/generated/integration-trace/trace-point-id";

/**
 * Debounce direction icon changes using the Messages/Keys table debounce times.
 *
 * - Raw direction is resolved from the usage map on every render.
 * - When direction changes, a debounce timer delays the update.
 * - When direction becomes null (idle), the last known direction is preserved
 *   for dimmed display.
 *
 * @param deviceType LiftType.ELEVATOR or LiftType.ESCALATOR — determines debounce map key prefix
 */
export default function useDirectionDebounce(
  usage: UsageMap,
  debounceMap: Map<number, number>,
  deviceType: LiftType.ELEVATOR | LiftType.ESCALATOR = LiftType.ELEVATOR,
): {
  directionIcon: string | null;
  lastDirectionIcon: string | null;
} {
  const rawDirection = usageToDirectionIcon(usage);

  const [debouncedDirection, setDebouncedDirection] = useState<string | null>(rawDirection);
  const lastDirectionRef = useRef<string | null>(rawDirection);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (rawDirection === debouncedDirection) {
      // Already in sync — nothing to debounce
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Look up debounce time for the new direction using numeric composite keys
    const dt = deviceType === LiftType.ELEVATOR ? DEVICE_ELEVATOR : DEVICE_ESCALATOR;
    const mc = MessageContent.Usage_UsageMessage;
    let debounceMs = 0;
    if (rawDirection === "elUp") {
      debounceMs = debounceMap.get(debounceKey(dt, mc, Usage.Up)) ?? 0;
    } else if (rawDirection === "elDown") {
      debounceMs = debounceMap.get(debounceKey(dt, mc, Usage.Down)) ?? 0;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (debounceMs <= 0) {
      // No debounce — update immediately
      // TP 418: FrontendDirectionDebounce — direction applied without debounce delay
      tracePointEmit(TracePointId.FrontendDirectionDebounce);
      setDebouncedDirection(rawDirection);
    } else {
      timerRef.current = setTimeout(() => {
        // TP 418: FrontendDirectionDebounce — debounced direction timer fired
        tracePointEmit(TracePointId.FrontendDirectionDebounce);
        setDebouncedDirection(rawDirection);
        timerRef.current = null;
      }, debounceMs);
    }
  }, [rawDirection, debounceMap, debouncedDirection, deviceType]);

  // Track last non-null direction for dimmed display
  useEffect(() => {
    if (debouncedDirection) {
      lastDirectionRef.current = debouncedDirection;
    }
  }, [debouncedDirection]);

  // Cleanup timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return {
    directionIcon: debouncedDirection,
    lastDirectionIcon: lastDirectionRef.current,
  };
}
